import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

function Login() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Preencha todos os campos.');
      return;
    }

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.error) {
      LogService.log(LOG_ACTIONS.LOGIN_FAIL, {
        status: 'error',
        errorMessage: result.error,
        userEmail: email,
        details: { email },
      });
      if (result.needsConfirmation) {
        navigate('/confirmar-email');
        return;
      }
      setError(result.error);
    } else {
      LogService.log(LOG_ACTIONS.LOGIN_SUCCESS, {
        details: { email },
        userEmail: email,
      });
      if (result.isNewUser) {
        navigate('/escolher-plano');
      } else {
        navigate('/');
      }
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    const result = await signInWithGoogle();
    if (result.error) {
      setGoogleLoading(false);
      setError(result.error);
    }
    // OAuth will redirect — no need to handle success here
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-secondary/95 to-primary/20 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo + tagline */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 no-underline justify-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-cta flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-base">QCO</span>
            </div>
            <span className="font-display font-bold text-3xl text-white">QuantoCustaObra</span>
          </Link>
          <p className="text-white/70 text-sm font-medium mt-3">Descubra quanto vai custar sua obra</p>
        </div>

        {/* Card */}
        <div className="bg-card rounded-2xl p-8 shadow-2xl shadow-black/20">
          <h1 className="text-2xl font-bold text-secondary text-center mb-1 font-display">Entrar</h1>
          <p className="text-muted-foreground text-center mb-6">Acesse sua conta</p>

          {/* Google OAuth */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-border bg-white text-foreground font-medium hover:bg-muted transition-all mb-4 cursor-pointer disabled:opacity-60"
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            Entrar com Google
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-border"></div>
            <span className="text-xs text-muted-foreground font-medium">ou entre com email</span>
            <div className="flex-1 h-px bg-border"></div>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full px-4 py-3 rounded-xl border border-border bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent focus:bg-card transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                className="w-full px-4 py-3 rounded-xl border border-border bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent focus:bg-card transition-all"
              />
            </div>

            <div className="text-right">
              <button type="button" className="text-sm text-accent hover:text-secondary cursor-pointer bg-transparent border-none">
                Esqueci minha senha
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white text-lg font-bold py-4 rounded-xl hover:opacity-90 active:bg-orange-700 transition-all duration-200 shadow-lg shadow-primary/20 cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : 'Entrar'}
            </button>
          </form>

          <p className="mt-6 text-center text-muted-foreground text-sm">
            Não tem conta?{' '}
            <Link to="/registro" className="text-accent font-semibold hover:text-secondary no-underline">
              Registre-se grátis
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
